import { memo, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useTranslation } from 'react-i18next';
import { K, apiLabel } from '../apiServiceI18n';
import ApiServiceTableRow from './ApiServiceTableRow';
import ApiServiceTableSkeletonBody from './ApiServiceTableSkeletonBody';
import { getTableColumns, getTableGrid } from './apiServiceTableLayout';

function compareRows(a, b, key) {
  if (key === 'createdTs') {
    return (a.createdTs ?? 0) - (b.createdTs ?? 0);
  }
  return String(a[key] ?? '').toLowerCase().localeCompare(String(b[key] ?? '').toLowerCase());
}

function columnHeaderLabel(t, labelKey) {
  if (
    labelKey.startsWith('LBL_') ||
    labelKey === K.WORKFLOW_GROUP ||
    labelKey === K.WORKFLOW ||
    labelKey === K.ACCOUNT
  ) {
    return apiLabel(labelKey);
  }
  return t(labelKey);
}

function ApiServiceTable({
  rows,
  showAccountCol,
  canModify,
  paginated = false,
  loading = false,
  skeletonRowCount = 10,
  onStatusToggle,
  onDetails,
  onPerformance,
  onWorkflow,
  onView,
  onEdit,
  onDelete,
  onExecutionHistory,
}) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const { gridCols, minWidth } = getTableGrid(showAccountCol);
  const cols = useMemo(() => getTableColumns(showAccountCol), [showAccountCol]);

  const sorted = useMemo(() => {
    if (loading) return [];
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = compareRows(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir, loading]);

  const handleSort = (key) => {
    if (loading || !key) return;
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <Box
      aria-busy={loading}
      sx={{
        border: paginated ? 'none' : '1px solid #d5d9e0',
        borderRadius: paginated ? 0 : 2,
        overflowX: 'auto',
        bgcolor: '#f6f7fa',
        boxShadow: paginated ? 'none' : 'inset 0 1px 0 rgba(255,255,255,0.65)',
      }}
    >
      <Box sx={{ minWidth }}>
        <Box
          role="row"
          sx={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            px: 1.5,
            py: 1.25,
            fontWeight: 700,
            fontSize: '0.78rem',
            letterSpacing: 0.2,
            textTransform: 'uppercase',
            color: '#fff',
            bgcolor: '#3d4654',
            borderBottom: '1px solid #2c333d',
          }}
        >
          {cols.map(({ labelKey, key }) => (
            <Box
              key={labelKey}
              onClick={() => handleSort(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.4,
                cursor: key && !loading ? 'pointer' : 'default',
                userSelect: 'none',
                opacity: loading ? 0.85 : 1,
                '&:hover': key && !loading ? { opacity: 0.9 } : {},
              }}
            >
              {columnHeaderLabel(t, labelKey)}
              {key && !loading &&
                (sortKey === key ? (
                  sortDir === 'asc' ? (
                    <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                  )
                ) : (
                  <UnfoldMoreIcon sx={{ fontSize: 14, opacity: 0.45 }} />
                ))}
            </Box>
          ))}
        </Box>

        {loading ? (
          <ApiServiceTableSkeletonBody
            showAccountCol={showAccountCol}
            rowCount={skeletonRowCount}
          />
        ) : sorted.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'white' }}>
            <Typography color="text.secondary">{t(K.API_SVC_NO_FILTER_MATCH)}</Typography>
          </Box>
        ) : (
          sorted.map((row) => (
            <ApiServiceTableRow
              key={row.id}
              row={row}
              gridCols={gridCols}
              showAccountCol={showAccountCol}
              canModify={canModify}
              onStatusToggle={onStatusToggle}
              onDetails={onDetails}
              onPerformance={onPerformance}
              onWorkflow={onWorkflow}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
              onExecutionHistory={onExecutionHistory}
            />
          ))
        )}
      </Box>
    </Box>
  );
}

export default memo(ApiServiceTable);
